/**
 * Navigation Service
 * Handles cross-feature navigation in a scalable and maintainable way
 * Follows industry best practices for service architecture
 */

class NavigationService {
  /**
   * Navigate to messaging with a specific user
   * @param {number} userId - The ID of the user to message
   * @param {string} username - The username for display purposes
   * @param {Object} navigate - React Router's navigate function
   */
  static navigateToUserMessage(userId, username, navigate) {
    if (!userId || !username || !navigate) {
      console.error('NavigationService: Missing required parameters for user message navigation');
      return;
    }

    // Stack IA: open new-message screen with recipient pre-selected
    navigate('/messages/new', { 
      state: { 
        action: 'new_message',
        recipientId: userId,
        recipientName: username,
        source: 'club_members'
      }
    });
  }

  /**
   * Navigate to messaging with a specific conversation
   * @param {number} conversationId - The ID of the conversation
   * @param {Object} navigate - React Router's navigate function
   */
  static navigateToConversation(conversationId, navigate) {
    if (!conversationId || !navigate) {
      console.error('NavigationService: Missing required parameters for conversation navigation');
      return;
    }

    navigate(`/messages/${conversationId}`);
  }

  /**
   * Navigate to club details
   * @param {number} clubId - The ID of the club
   * @param {Object} navigate - React Router's navigate function
   */
  static navigateToClub(clubId, navigate) {
    if (!clubId || !navigate) {
      console.error('NavigationService: Missing required parameters for club navigation');
      return;
    }

    navigate(`/clubs/${clubId}`);
  }

  /**
   * Navigate to hunt details
   * @param {number} huntId - The ID of the hunt
   * @param {Object} navigate - React Router's navigate function
   */
  static navigateToHunt(huntId, navigate) {
    if (!huntId || !navigate) {
      console.error('NavigationService: Missing required parameters for hunt navigation');
      return;
    }

    navigate(`/hunts/${huntId}`);
  }

  /**
   * Get navigation state from location
   * @param {Object} location - React Router's location object
   * @returns {Object} Navigation state
   */
  static getNavigationState(location) {
    return location?.state || {};
  }

  /**
   * Clear navigation state
   * @param {Object} navigate - React Router's navigate function
   * @param {string} path - Path to navigate to
   */
  static clearNavigationState(navigate, path) {
    navigate(path, { replace: true, state: null });
  }
}

export default NavigationService;
